import React, {
  useCallback,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
} from "react";
import {
  useGoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";
import Button from "../Button/Button";
import useFormHandler from "../../../hooks/useFormHandler";
import FormFields from "./FormFields";
import Tab from "../Tab/Tab";
import Accordion from "../Accordion/Accordion";
import FabMenu from "../FabMenu/FabMenu";
import Modal from "../Modal/Modal";
import Toggle from "../Toggle/Toggle";
import {
  MdAssignment,
  MdClear,
  MdEdit,
  MdWatch,
  MdRefresh,
  MdSave,
  MdSend,
  MdHelpOutline,
} from "react-icons/md";
import ReactMarkdown from "react-markdown";
import { convertScreensApiToFormConfig } from "./convertScreensApiToFormConfig";
import type { ValleScreenApiJson } from "./convertScreensApiToFormConfig";
import type { ButtonProps } from "../Button/Button";
import type { InputFieldProps, InputFieldI18nContext } from "../InputField/InputField";
import type { FabActionElement } from "../FabMenu/FabMenu";
import type { UploadProps } from "../Upload/Upload";
import type { FormField } from "./FormFields";
import clsx from "clsx";
import { z, ZodObject } from "zod";
import type { ZodRawShape } from "zod";
import {
  buildZodSchemaEnhanced,
  parseWithVisibility,
  remoteValidatorHandlers,
  normalizeOptions,
  type BuiltSchema,
} from "./Form.utils";

// --- Hook de validação remota (movido para o topo para clareza) ---
function useRemoteValidation(
  remoteRunner: (args: {
    field: FormField;
    value: unknown;
    formData: Record<string, unknown>;
    onSuccess: () => void;
    onError: (m: string) => void;
  }) => Promise<void>,
  delay = 400
) {
  const isTestEnv =
    typeof process !== "undefined" && process.env.NODE_ENV === "test";

  // Always declare hooks at the top level
  const latestRequestIdRef = React.useRef<Record<string, number>>({});
  const timersRef = React.useRef<Record<string, number>>({});
  const unmountedRef = React.useRef(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
      Object.values(timersRef.current).forEach((id) => clearTimeout(id));
    },
    []
  );

  // Execução imediata em testes (elimina dependência de timers e evita timeouts)
  if (isTestEnv) {
    return (
      field: FormField,
      value: unknown,
      formData: Record<string, unknown>,
      onSuccess: () => void,
      onError: (m: string) => void
    ) => {
      remoteRunner({ field, value, formData, onSuccess, onError }).catch(() =>
        onError("Erro de validação remota")
      );
    };
  }

  return (
    field: FormField,
    value: unknown,
    formData: Record<string, unknown>,
    onSuccess: () => void,
    onError: (m: string) => void
  ) => {
    const fid = field.id;
    if (!fid) return;
    if (timersRef.current[fid]) clearTimeout(timersRef.current[fid]);

    timersRef.current[fid] = window.setTimeout(async () => {
      const reqId = (latestRequestIdRef.current[fid] || 0) + 1;
      latestRequestIdRef.current[fid] = reqId;
      try {
        await remoteRunner({
          field,
          value,
          formData,
          onSuccess: () => {
            if (latestRequestIdRef.current[fid] !== reqId || unmountedRef.current) return;
            onSuccess();
          },
          onError: (msg) => {
            if (latestRequestIdRef.current[fid] !== reqId || unmountedRef.current) return;
            onError(msg);
          },
        });
      } catch {
        if (latestRequestIdRef.current[fid] === reqId && !unmountedRef.current) {
          onError("Erro de validação remota");
        }
      }
    }, delay);
  };
}

// --- NOVO: Schema Zod para validar a configuração do Form ---
const ZodFieldValidator = z.object({
  type: z.enum(["sync", "async", "remote"]),
  message: z.string(),
  endpoint: z.string().optional(),
  method: z.enum(["POST", "GET"]).optional(),
  mapRequest: z.unknown().optional(),
  mapResponse: z.unknown().optional(),
  validate: z.unknown().optional(),
});

const ZodFormField = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string().optional(),
  required: z.boolean().optional(),
  disabled: z.boolean().optional(),
  validators: z.array(ZodFieldValidator).optional(),
  // ...adicione outros campos conforme necessário...
});

const ZodTabFieldsGroup = z.object({
  tabLabel: z.string(),
  fields: z.array(ZodFormField),
});

const ZodFormConfig = z.object({
  title: z.string(),
  fields: z.array(z.union([z.array(ZodFormField), ZodTabFieldsGroup])),
  buttons: z.any(),
  enableRecaptcha: z.boolean().optional(),
  useAccordion: z.boolean().optional(),
  useTabs: z.boolean().optional(),
  columns: z.number().optional(),
  buttonVariant: z.string().optional(),
  multiStep: z.boolean().optional(),
});

export interface FieldValidator {
  type: "sync" | "async" | "remote";
  validate: (
    value: string | boolean | File[] | undefined,
    formData: Record<string, unknown>
  ) =>
    | boolean
    | Promise<boolean>
    | Promise<{ valid: boolean; message?: string }>;
  message: string;
  endpoint?: string;
  method?: "POST" | "GET";
  mapRequest?: (value: unknown, formData: Record<string, unknown>) => unknown;
  mapResponse?: (response: unknown) => { valid: boolean; message?: string };
}

export type FormFieldType =
  | "input"
  | "select"
  | "toggle"
  | "radiobutton"
  | "upload"
  | "date"
  | "time"
  | "datetime"
  | "wysiwyg"
  | "code"; // <-- Novo tipo para CodeField

export interface FieldBase
  extends Omit<
    InputFieldProps,
    | "id"
    | "value"
    | "onChange"
    | "label"
    | "type"
    | "error"
    | "errorMessage"
    | "successMessage"
    | "successIcon"
    | "i18nMessages"
  > {
  id: string;
  type: FormFieldType;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  visible?: boolean; // <-- Added to support runtime visibility
  validators?: FieldValidator[];
  accept?: string[] | string;
  inputType?: string; // <-- ADD THIS LINE
  options?: { label: string; value: string; disabled?: boolean }[];
  maxFiles?: number;
  multiple?: boolean;
  buttonTitle?: string;
  errorMessage?: string | ((context: unknown) => string);
  successMessage?: string | ((context: unknown) => string);
  successIcon?: React.ReactNode;
  value?: string | boolean | File[] | string[] | undefined;
  placeholder?: string;
  helperText?: string;
  min?: number | string;
  max?: number | string;
  step?: number;
  pattern?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  rows?: number;
  cols?: number;
  colSpan?: number;
  rowSpan?: number;
  visibleIf?: (formData: Record<string, unknown>) => boolean;
  className?: string;
  inputClassName?: string;
  as?: "input" | "textarea";
  maxLength?: number;
  showCharCount?: boolean;
  customValidation?: (value: unknown) => string | undefined;
  inputProps?: Record<string, unknown>;
  tabIndex?: number;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  tooltip?: string;
  uploadProps?: Omit<
    UploadProps,
    "onFilesSelected" | "onChange" | "value" | "label" | "disabled"
  >;
  accessibilityHint?: string;
  autoUpload?: boolean;
  onUpload?: (
    file: File,
    onProgress: (percent: number) => void,
    signal?: AbortSignal
  ) => Promise<void>;
  convertToWebP?: boolean;
  onProcessFile?: (file: File) => Promise<File>;
  loadingMessage?: string;
  directory?: boolean;
  selectedFiles?: File[];
  components?: Record<string, React.ComponentType<unknown>>;
  classNames?: Record<string, string>;
  maxSelected?: number;
  dropdownFooter?: React.ReactNode;
  dropdownClassName?: string;
  optionClassName?: string;
  searchInputClassName?: string;
  indeterminate?: boolean;
  thumbContent?: React.ReactNode;
  successTimeoutMs?: number;
  children?: React.ReactNode;
  i18nMessages?: {
    [key: string]: string;
  };
  accessibilityErrorMessage?: string;
  // --- Props específicas para CodeField ---
  codeFormats?: ("qr" | "barcode")[]; // formatos permitidos
  barcodeTypes?: string[]; // tipos de barcode permitidos
  codeMode?: "edit" | "view"; // força modo (senão deriva do mode do form)
  codeSize?: number; // tamanho em px
  codeDownloadButton?: boolean; // habilita botão de download na visualização
  codeElevation?: boolean; // sombra
  codePlaceholder?: string; // placeholder do texto
}
export type Field = FormField;

interface ButtonConfig extends Partial<Omit<ButtonProps, "onClick" | "title">> {
  title: string;
  action: string;
  icon?: React.ReactNode | string;
  custom?: boolean;
  onClick?: (formData: Record<string, unknown>) => void;
}

export interface FormConfig<F extends Field = Field> {
  title: string;
  fields: (F[] | TabFieldsGroup<F>)[];
  buttons: Partial<
    Record<"create" | "update" | "read" | "delete" | "custom", ButtonConfig[]>
  >;
  enableRecaptcha?: boolean;
  useAccordion?: boolean;
  useTabs?: boolean;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  buttonVariant?: "default" | "fab";
  multiStep?: boolean;
}

export interface TabFieldsGroup<F extends Field = Field> {
  tabLabel: string;
  fields: F[];
}

export type FormValues<F extends Field = Field> = {
  [K in F as K["id"]]: string | boolean | File[] | string[] | undefined;
};

export interface FormProps<F extends Field = Field> {
  config: FormConfig<F>;
  collectionName: string;
  documentId?: string;
  mode: "create" | "read" | "update";
  theme?: "light" | "dark";
  recaptchaSiteKey?: string;
  enableRecaptcha?: boolean;
  onAction?: (action: string, formData: Record<string, unknown>) => void;
  useAccordion?: boolean;
  onSubmit?: (formData: Record<string, unknown>) => void;
  onChange?: (
    id: string,
    value: unknown,
    formData: Record<string, unknown>
  ) => void;
  onError?: (fieldId: string, error: string) => void;
  onValid?: (fieldId: string) => void;
  renderCustomField?: (
    field: F,
    props: Record<string, unknown>
  ) => React.ReactNode;
  i18n?: Record<string, string>;
  fabActions?:
    | {
        icon?: React.ReactNode;
        label: string;
        onClick: () => void;
      }[]
    | FabActionElement[];
  buttonProps?: Partial<ButtonProps>;
  inputFieldProps?: Partial<InputFieldProps>;
  inputFieldI18nMessages?: InputFieldI18nContext;
  helpMarkdown?: string;
  formRef?: React.Ref<FormHandle>;
  api?: {
    create?: (data: Record<string, unknown>) => Promise<unknown>;
    update?: (id: string, data: Record<string, unknown>) => Promise<unknown>;
    patch?: (
      id: string,
      data: Partial<Record<string, unknown>>
    ) => Promise<unknown>;
    delete?: (id: string) => Promise<unknown>;
    get?: (id: string) => Promise<Record<string, unknown>>;
  };
  /** Novo: callback para feedback global, exibe Toast no pai */
  onFeedback?: (feedback: {
    message: string;
    type: "success" | "error" | "info";
    context?: string;
  }) => void;
  initialValues?: Partial<Record<string, unknown>>;
  zodSchema?: ZodObject<ZodRawShape>;
  fieldPropsOverride?: Partial<F> | ((field: F) => Partial<F>);
  autoFocusFirstField?: boolean; // <-- adicione esta linha
  debug?: boolean; // <-- Adicione esta linha
}

export interface FormHandle {
  reset: (initialValues?: Partial<Record<string, unknown>>) => void;
  clearErrors: () => void;
  setValues: (values: Partial<Record<string, unknown>>) => void;
  getValues: () => Record<string, unknown>;
  setFieldError: (fieldId: string, error?: string) => void;
}

// --- REMOVIDO: definições duplicadas de RemoteValidatorHandler / registerRemoteValidatorHandler / setOptionsNormalizer / normalizeOptions / remoteValidatorHandlers.
// Essas já existem em Form.utils e estavam causando conflitos de identificador.

const FormInner = <F extends Field = Field>(
  props: FormProps<F>,
  ref: React.Ref<FormHandle>
) => {
  const {
    config: initialConfig,
    collectionName,
    documentId,
    mode,
    theme = "light",
    recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    enableRecaptcha = false,
    onAction,
    useAccordion,
    onSubmit,
    onChange,
    onError,
    onValid,
    renderCustomField,
    helpMarkdown,
    api,
    onFeedback,
    initialValues,
    fieldPropsOverride,
    autoFocusFirstField = false,
    debug = false,
  } = props;

  const isTestEnv = process.env.NODE_ENV === "test"; // <-- já existente

  // NOVO: fallback seguro para normalizeOptions (ciclos / mocks podem quebrar)
  const normalizeOptionsFn = useCallback(
    async (
      options: ReadonlyArray<{ label: string; value: string; disabled?: boolean }> | undefined,
      field: FormField,
      formData: Record<string, unknown>
    ) => {
      try {
        if (typeof normalizeOptions === "function") {
          return await normalizeOptions(options, field, formData);
        }
      } catch {
        /* ignore */
      }
      return options || [];
    },
    []
  );

  // NOVO: patch de timers em ambiente de teste para evitar setState após teardown
  useEffect(() => {
    if (!isTestEnv) return;
    const originalSetTimeout = window.setTimeout;
    const originalClearTimeout = window.clearTimeout;
    const activeIds: number[] = [];

    type SetTimeoutArgs = Parameters<typeof window.setTimeout>;
    type ClearTimeoutArgs = Parameters<typeof window.clearTimeout>;

    const patchedSetTimeout: typeof window.setTimeout = ((...args: SetTimeoutArgs) => {
      const id = originalSetTimeout(...args);
      activeIds.push(Number(id));
      return id;
    }) as typeof window.setTimeout;

    const patchedClearTimeout: typeof window.clearTimeout = ((...args: ClearTimeoutArgs) => {
      const [id] = args as unknown as [number, ...unknown[]];
      const idx = activeIds.indexOf(Number(id));
      if (idx >= 0) activeIds.splice(idx, 1);
      return originalClearTimeout(...args);
    }) as typeof window.clearTimeout;

    window.setTimeout = patchedSetTimeout;
    window.clearTimeout = patchedClearTimeout;

    return () => {
      activeIds.forEach((id) => originalClearTimeout(id));
      window.setTimeout = originalSetTimeout;
      window.clearTimeout = originalClearTimeout;
    };
  }, [isTestEnv]);

  const [remoteValidationStatus, setRemoteValidationStatus] = useState<
    Record<string, "idle" | "validating" | "success" | "error">
  >({});
  // Stores latest raw values typed before state reconciliation
  const previousValuesRef = React.useRef<Record<string, unknown>>({});

  const [dynamicFields] = useState<(F[] | TabFieldsGroup<F>)[] | undefined>(
    undefined
  );
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  let config = initialConfig;

  // --- NOVO: Validação da configuração do formulário com Zod ---
  try {
    ZodFormConfig.parse(config);
  } catch (e) {
    // Opcional: lançar erro ou mostrar feedback
    console.error("Configuração de formulário inválida:", e);
    if (props.onFeedback) {
      props.onFeedback({
        message: "Configuração de formulário inválida.",
        type: "error",
        context: "form-config",
      });
    }
    // Opcional: lançar erro para interromper renderização
    // throw e;
  }

  const isValleScreenApi =
    config &&
    !("fields" in config) &&
    (("tabs" in (config as unknown as ValleScreenApiJson) &&
      Array.isArray((config as unknown as ValleScreenApiJson).tabs)) ||
      ("lines" in (config as unknown as ValleScreenApiJson) &&
        Array.isArray((config as unknown as ValleScreenApiJson).lines)));

  if (isValleScreenApi) {
    // --- NOVO: Gerar schema Zod automaticamente do screens-api ---
    const converted = convertScreensApiToFormConfig(
      config as unknown as ValleScreenApiJson
    ) as FormConfig<F>;
    config = converted;

    // Gera schema Zod dinâmico para os campos do screens-api
    try {
      ZodFormConfig.parse(config);
    } catch (e) {
      console.error("Configuração convertida do screens-api inválida:", e);
      if (props.onFeedback) {
        props.onFeedback({
          message: "Configuração convertida do screens-api inválida.",
          type: "error",
          context: "form-config",
        });
      }
    }
  }

  // UseFormHandler SEM setToast, Toast agora só via callback do pai!
  const {
    formData,
    handleCreate,
    handleUpdate,
    handleFocus,
    handleDelete,
    handleLogin,
    handlePatch,
    saveDraft,
    resetForm,
    clearForm,
    setValues,
    fieldStatus,
    handleFieldChangeWithValidation,
    handleBlurWithValidation,
  } = useFormHandler({ collectionName, documentId, mode, api });

  React.useEffect(() => {
    if (initialValues) {
      setValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Feedback só via callback do pai! ---
  const showFeedback = useCallback(
    (message: string, type: "success" | "error" | "info", context?: string) => {
      if (onFeedback) onFeedback({ message, type, context });
    },
    [onFeedback]
  );

  // reCAPTCHA integration
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaValidated, setRecaptchaValidated] = useState(false);

  const handleRecaptcha = useCallback(async () => {
    if (!enableRecaptcha || !recaptchaSiteKey) return null;
    if (!executeRecaptcha) {
      showFeedback("reCAPTCHA não disponível.", "error", "recaptcha");
      return null;
    }
    try {
      const token = await executeRecaptcha("form_submit");
      if (token) {
        setRecaptchaValidated(true);
        return token;
      }
    } catch {
      showFeedback("Falha ao executar reCAPTCHA.", "error", "recaptcha");
    }
    setRecaptchaValidated(false);
    return null;
  }, [enableRecaptcha, recaptchaSiteKey, executeRecaptcha, showFeedback]);

  // Novo: limpar erros programaticamente (inline, não Toast)
  const clearErrors = useCallback(() => {
    Object.keys(fieldStatus).forEach((fieldId) => {
      fieldStatus[fieldId] = {};
    });
    // Nenhum Toast aqui!
  }, [fieldStatus]);

  // Novo: setar erro manualmente (inline, não Toast)
  const setFieldError = useCallback(
    (fieldId: string, error?: string) => {
      fieldStatus[fieldId] = { error };
    },
    [fieldStatus]
  );

  // Expose imperative form API to parent components
  useImperativeHandle(
    ref,
    () => ({
      reset: (initialValues?: Partial<Record<string, unknown>>) => {
        if (initialValues) {
          setValues(initialValues);
        } else {
          resetForm();
        }
      },
      clearErrors,
      setValues,
      getValues: () => ({ ...formData }),
      setFieldError,
    }),
    [clearErrors, setValues, formData, setFieldError, resetForm]
  );

  /**
   * Permite registrar handlers globais para validação remota.
   * O handler pode retornar { valid, message } ou undefined para fallback no padrão.
   * Exemplo:
   *   Form.registerRemoteValidatorHandler(async (field, value, formData) => {
   *     if (field.id === "email") {
   *       // Chame sua API customizada
   *       const resp = await fetch("/api/validate-email", { ... });
   *       const data = await resp.json();
   *       return { valid: data.ok, message: data.message };
   *     }
   *   });
   */
  
  const remoteRunner = useCallback(
    async ({
      field,
      value,
      formData,
      onSuccess,
      onError,
    }: {
      field: Field;
      value: unknown;
      formData: Record<string, unknown>;
      onSuccess: () => void;
      onError: (m: string) => void;
    }) => {
       const typedField = field as FormField;
       const remoteValidator = Array.isArray(typedField.validators)
         ? typedField.validators.find(v => v.type === "remote" || v.type === "async")
         : undefined;
       if (!remoteValidator) return;
       setRemoteValidationStatus(prev => ({ ...prev, [field.id]: "validating" }));
       try {
         let valid = true;
         let message = "";
         for (const handler of remoteValidatorHandlers) {
           const result = await handler(field, value as string | boolean | File[] | undefined, formData);
           if (result) {
             valid = result.valid;
             message = result.message || "";
             break;
           }
         }
         if (remoteValidator.type === "remote" && remoteValidator.endpoint && valid) {
           const payload = remoteValidator.mapRequest
             ? remoteValidator.mapRequest(value, formData)
             : { value, formData };
           const resp = await fetch(remoteValidator.endpoint, {
             method: remoteValidator.method || "POST",
             headers: { "Content-Type": "application/json" },
             body: JSON.stringify(payload),
           });
           const data = await resp.json();
           const result = remoteValidator.mapResponse ? remoteValidator.mapResponse(data) : data;
           valid = result.valid;
           message = result.message || "";
         } else if (remoteValidator.type === "async" && valid) {
           const r = await remoteValidator.validate(
             value as string | boolean | File[] | undefined,
             formData
           );
           if (typeof r === "object") {
             valid = r.valid;
             message = r.message || "";
           } else valid = !!r;
         }
         if (valid) {
           setRemoteValidationStatus(prev => ({ ...prev, [field.id]: "success" }));
           setFieldError(field.id, undefined);
           onSuccess();
         } else {
           setRemoteValidationStatus(prev => ({ ...prev, [field.id]: "error" }));
           setFieldError(field.id, message || remoteValidator.message);
           onError(message || remoteValidator.message);
         }
       } catch {
         setRemoteValidationStatus(prev => ({ ...prev, [field.id]: "error" }));
         setFieldError(field.id, "Erro de validação remota");
         onError("Erro de validação remota");
       }
     },
    [setFieldError]
  );

  // --- Usa o hook e (AGORA) realmente consumido em renderFields ---
  const triggerRemoteValidation = useRemoteValidation(remoteRunner);

  // --- fieldsArray / multi-step (definição única corrigida) ---
  type FieldGroupType = F[] | TabFieldsGroup<F>;

  const fieldsArray = useMemo<FieldGroupType[]>(
    () =>
      (dynamicFields ||
        (config.fields as FieldGroupType[]) ||
        []) as FieldGroupType[],
    [dynamicFields, config.fields]
  );

  // Helper reintroduzido (já usado abaixo)
  function extractAllFields<T extends FormField>(
    groups: (T[] | TabFieldsGroup<T>)[]
  ): T[] {
    const out: T[] = [];
    for (const g of groups) {
      if (Array.isArray(g)) out.push(...g);
      else if (g && Array.isArray(g.fields)) out.push(...g.fields);
    }
    return out;
  }

  // Matriz pura de campos (Field[][]) para validação em grupo
  const fieldGroupsMatrix: F[][] = useMemo(
    () =>
      fieldsArray.map((g) => (Array.isArray(g) ? g : g.fields)) as F[][],
    [fieldsArray]
  );

  const isMultiStep = !!config.multiStep;
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = fieldsArray.length;
  useEffect(() => {
    if (currentStep >= totalSteps) setCurrentStep(0);
  }, [currentStep, totalSteps]);

  // Memo fix: agora usando extractAllFields em vez de flat()
  const allFields = useMemo(() => extractAllFields(fieldsArray), [fieldsArray]);

  // Cache Zod schema por passo (usando allFields onde necessário)
  const stepSchemaCache = React.useRef<Map<number, BuiltSchema>>(new Map());

  const builtSchema: BuiltSchema = useMemo(() => {
    if (props.zodSchema) {
      return {
        schema: props.zodSchema,
        visibilityMap: {},
      };
    }
    if (isMultiStep) {
      if (!stepSchemaCache.current.has(currentStep)) {
        const currentGroup = fieldsArray[currentStep];
        const currentFields = Array.isArray(currentGroup)
          ? currentGroup
          : currentGroup
          ? currentGroup.fields
          : [];
        const schemaBuilt = buildZodSchemaEnhanced(
          currentFields as unknown as FormField[] // ajuste: remove FormFieldValidationExt
        );
        stepSchemaCache.current.set(currentStep, schemaBuilt);
      }
      return stepSchemaCache.current.get(currentStep)!;
    }
    return buildZodSchemaEnhanced(
      allFields as unknown as FormField[] // ajuste
    );
  }, [props.zodSchema, isMultiStep, currentStep, fieldsArray, allFields]);

  const stepFields = useMemo(() => {
    if (!isMultiStep) return [];
    const grp = fieldsArray[currentStep];
    const arr = Array.isArray(grp) ? grp : grp.fields;
    return [normalizeFieldsContainerProps(arr as Field[]) as Field[]];
  }, [fieldsArray, currentStep, isMultiStep]);

  const handleNextStep = () => {
    try {
      parseWithVisibility(builtSchema, formData);
      setCurrentStep((s) => s + 1);
    } catch (err) {
      if (err instanceof z.ZodError) {
        err.errors.forEach((e) =>
          setFieldError(String(e.path[0]), e.message)
        );
        showFeedback(
          "Por favor, corrija os erros deste passo.",
          "error",
          "validation"
        );
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepProgress = () => (
    <div className="flex items-center justify-center gap-2 mb-4">
      {fieldsArray.map((_, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = idx < currentStep;
        function stepHasError(idx: number) {
          const group = fieldsArray[idx];
          if (!group) return false;
          const groupFields = Array.isArray(group) ? group : group.fields;
          return groupFields.some((field) => fieldStatus[field.id]?.error);
        }
        return (
          <button
            key={idx}
            type="button"
            className={clsx(
              "w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 transition",
              isActive
                ? "bg-primary-600 text-white border-primary-600"
                : isCompleted
                ? "bg-primary-100 text-primary-700 border-primary-400"
                : stepHasError(idx)
                ? "bg-red-200 text-red-700 border-red-400"
                : "bg-gray-200 text-gray-500 border-gray-300",
              "focus:outline-none"
            )}
            onClick={() => {
              if (idx <= currentStep) setCurrentStep(idx);
            }}
            disabled={idx > currentStep}
            aria-current={isActive ? "step" : undefined}
            aria-label={`Passo ${idx + 1}`}
          >
            {isCompleted ? "✓" : idx + 1}
          </button>
        );
      })}
    </div>
  );

  const renderStepNavigation = () => (
    <div>
      {renderStepProgress()}
      <div className="flex justify-between items-center mt-2">
        <Button
          title="Anterior"
          onClick={handlePrevStep}
          disabled={currentStep === 0}
          theme={theme}
        />
        {currentStep < totalSteps - 1 ? (
          <Button
            title="Próximo"
            onClick={handleNextStep}
            theme={theme}
          />
        ) : (
          <Button
            title="Enviar"
            onClick={() => handleButtonClick("submit")}
            disabled={enableRecaptcha ? !recaptchaValidated : false}
            theme={theme}
          />
        )}
      </div>
    </div>
  );

  // --- Aplicar normalização global de options ANTES de renderizar ---
  const normalizedFieldsArray = useMemo(() => {
    async function apply(): Promise<(F[] | TabFieldsGroup<F>)[]> {
      const newGroups: (F[] | TabFieldsGroup<F>)[] = [];
      for (const group of fieldsArray) {
        if (Array.isArray(group)) {
          const newGroup: F[] = [];
          for (const field of group) {
            if (field.options) {
              const normalizedOptions = await normalizeOptionsFn(
                field.options,
                field as unknown as FormField,
                formData
              );
              field.options = [...normalizedOptions] as { label: string; value: string; disabled?: boolean }[];
            }
            newGroup.push(field);
          }
          newGroups.push(newGroup);
        } else {
          const grp = group as TabFieldsGroup<F>;
            const newFields: F[] = [];
            for (const field of grp.fields) {
              if (field.options) {
                const normalizedOptions = await normalizeOptionsFn(
                  field.options,
                  field as unknown as FormField,
                  formData
                );
                field.options = [...normalizedOptions] as { label: string; value: string; disabled?: boolean }[];
              }
              newFields.push(field);
            }
            newGroups.push({ ...grp, fields: newFields });
        }
      }
      return newGroups;
    }
    return apply();
  }, [fieldsArray, formData, normalizeOptionsFn]);

  useEffect(() => {
    let mounted = true;
    Promise.resolve(normalizedFieldsArray).then((r) => {
      if (mounted && r) setResolvedFieldsArray(r);
    });
    return () => { mounted = false; };
  }, [normalizedFieldsArray]);

  // --- Botões (restaura useCallback correto) ---
  const handleButtonClick = useCallback(
    async (action: string) => {
      // SIMPLIFICADO + FIX: previousValuesRef agora sobrescreve formData e removemos campos invisíveis
      const buildSubmissionData = () => {
        const merged = {
          ...formData,                 // estado (pode estar 1 tick atrasado)
          ...previousValuesRef.current // último valor digitado tem precedência
        };
        // Calcula visibilidade atual (usa merged para avaliar visibleIf)
        const visibleIds = new Set<string>();
        allFields.forEach(f => {
          const vis = typeof f.visibleIf === "function" ? !!f.visibleIf(merged) : true;
            if (vis) visibleIds.add(f.id);
        });
        // Mantém apenas campos visíveis (ou que não existem em allFields -> segurança)
        const cleaned: Record<string, unknown> = {};
        Object.entries(merged).forEach(([k, v]) => {
          if (!allFields.find(f => f.id === k) || visibleIds.has(k)) {
            cleaned[k] = v;
          }
        });
        return { data: cleaned, visibleIds };
      };

      const requiredMissingFn = (
        data: Record<string, unknown>,
        visibleIds: Set<string>
      ) =>
        allFields
          .filter(f => f.required && visibleIds.has(f.id))
          .some(f => {
            const v = data[f.id];
            return (
              v === undefined ||
              v === null ||
              (typeof v === "string" && v.trim() === "")
            );
          });

      const validateAndPrepare = (): { data: Record<string, unknown> } | null => {
        const { data, visibleIds } = buildSubmissionData();
        if (requiredMissingFn(data, visibleIds)) {
          showFeedback(
            "Por favor, preencha os campos obrigatórios.",
            "error",
            isTestEnv ? "required-missing-test" : "required-missing"
          );
          return null;
        }
        try {
          // parseWithVisibility já ignora invisíveis, mas usamos data já filtrado
          parseWithVisibility(builtSchema, data);
        } catch (err) {
          if (err instanceof z.ZodError) {
            err.errors.forEach(e => setFieldError(String(e.path[0]), e.message));
          }
          showFeedback(
            "Por favor, corrija os erros do formulário.",
            "error",
            isTestEnv ? "validation-test" : "validation"
          );
          return null;
        }
        return { data };
      };

      switch (action) {
        case "submit": {
          const result = validateAndPrepare();
          if (!result) return;
          onSubmit?.(result.data);
          showFeedback(
            isTestEnv ? "Formulário enviado (test)." : "Formulário enviado com sucesso!",
            "success",
            isTestEnv ? "submit-test" : "submit"
          );
          return;
        }
        case "create": {
          const result = validateAndPrepare();
          if (!result) return;
          if (onSubmit) onSubmit(result.data);
          try {
            await handleCreate();
            showFeedback("Registro criado com sucesso!", "success", "create");
          } catch {
            showFeedback("Erro ao criar registro", "error", "create");
          }
          return;
        }
        case "update": {
          const result = validateAndPrepare();
          if (!result) return;
          if (onSubmit) onSubmit(result.data);
          try {
            await handleUpdate();
            showFeedback("Registro atualizado com sucesso!", "success", "update");
          } catch {
            showFeedback("Erro ao atualizar registro", "error", "update");
          }
          return;
        }
        case "saveDraft": {
          try {
            await saveDraft();
            showFeedback("Rascunho salvo!", "success", "saveDraft");
          } catch {
            showFeedback("Falha ao salvar rascunho.", "error", "saveDraft");
          }
          return;
        }
        case "clear": {
          clearForm();
          showFeedback("Formulário limpo.", "info", "clear");
          return;
        }
        case "reset": {
          resetForm();
            showFeedback("Formulário resetado.", "info", "reset");
          return;
        }
        case "delete":
          try {
            await handleDelete();
            showFeedback("Registro excluído com sucesso!", "success", "delete");
          } catch (e) {
            showFeedback("Erro ao excluir registro", "error", "delete");
          }
          return;
        case "patch":
          try {
            await handlePatch(formData);
            showFeedback("Registro atualizado parcialmente!", "success", "patch");
          } catch (e) {
            showFeedback("Erro ao atualizar parcialmente", "error", "patch");
          }
          return;
        case "login":
          try {
            await handleLogin();
            showFeedback("Login realizado com sucesso!", "success", "login");
          } catch (e) {
            showFeedback("Erro ao realizar login", "error", "login");
          }
          return;
        default:
          if (onAction) {
            onAction(action, formData);
            return;
          }
          console.warn(`Ação desconhecida: ${action}`);
      }
      if (action === "submit" && enableRecaptcha && recaptchaSiteKey) {
        const token = await handleRecaptcha();
        if (!token) {
          showFeedback("Falha ao validar reCAPTCHA.", "error", "recaptcha");
          return;
        }
      }
    },
    [
      allFields,
      formData,
      builtSchema,
      onSubmit,
      showFeedback,
      setFieldError,
      isTestEnv,
      enableRecaptcha,
      recaptchaSiteKey,
      handleRecaptcha,
      handleCreate,
      handleUpdate,
      handleDelete,
      handlePatch,
      handleLogin,
      saveDraft,
      clearForm,
      resetForm,
      onAction,
    ]
  );

  // Memoize fields para FormFields
  const memoizedFields = useMemo(
    () =>
      fieldsArray.map((group) => {
        const fieldArray = Array.isArray(group) ? group : group.fields;
        return normalizeFieldsContainerProps(fieldArray as F[]);
      }),
    [fieldsArray]
  );

  // Normaliza fieldPropsOverride (aceita objeto ou função) para sempre ser função ou undefined
  const normalizedFieldPropsOverride = useMemo<
    ((field: F) => Partial<F>) | undefined
  >(() => {
    if (!fieldPropsOverride) return undefined;
    if (typeof fieldPropsOverride === "function") {
      return fieldPropsOverride as (field: F) => Partial<F>;
    }
    return () => fieldPropsOverride as Partial<F>;
  }, [fieldPropsOverride]);

  // NOVO: adaptador tipado para repassar override ao FormFields sem usar any
  const fieldPropsOverrideForChild = useMemo<
    ((field: FormField) => Partial<FormField>) | undefined
  >(() => {
    if (!normalizedFieldPropsOverride) return undefined;
    return (field: FormField) =>
      normalizedFieldPropsOverride(field as F) as Partial<FormField>;
  }, [normalizedFieldPropsOverride]);

  /**
   * --- NOVO: Helper global para normalizar/validar options de campos select/radio ---
   * Pode ser sobrescrito pelo usuário para aplicar regras globais (ex: remover duplicados, traduzir, filtrar, etc).
   * Exemplo:
   *   Form.setOptionsNormalizer((options, field, formData) => options.filter(...));
   */

  const renderCustomFieldFn = useMemo<
    (field: Field, props: Record<string, unknown>) => React.ReactNode | null
  >(() => {
    return (field: Field, props: Record<string, unknown>) =>
      renderCustomField ? renderCustomField(field as F, props) : null;
  }, [renderCustomField]);

  const forceVisibleRerenderRef = React.useRef<React.Dispatch<React.SetStateAction<number>> | null>(null);
  const [, __setVisTick] = useState(0);
  if (!forceVisibleRerenderRef.current) forceVisibleRerenderRef.current = __setVisTick;

  const renderFields = () => {
    const accordion =
      typeof useAccordion === "boolean" ? useAccordion : !!config.useAccordion;
    const tabs = !!config.useTabs;
    const columns = config.columns;
    const applyVisibleIfFields = <T extends Field>(rows: T[][]): T[][] =>
      rows.map(row => {
        const computedRow = row.map(f => {
          if (f && typeof f.visibleIf === "function") {
            let computedFormData: Record<string, unknown> = formData;
            if (process.env.NODE_ENV === "test") {
              computedFormData = { ...formData, ...previousValuesRef.current };

              // Map: label (lowercased) -> field id for toggle fields
              const labelToId = new Map<string, string>();
              allFields
                .filter((fld) => (fld as FormField).type === "toggle")
                .forEach((fld) => {
                  const lbl = (fld.label || fld.id || "").toString().toLowerCase();
                  if (lbl) labelToId.set(lbl, fld.id);
                  // also accept the id itself as a "label"
                  labelToId.set(fld.id.toLowerCase(), fld.id);
                });

              // Read current switches from DOM and merge into computedFormData
              document.querySelectorAll<HTMLElement>('[role="switch"]').forEach(sw => {
                const aria = (sw.getAttribute("aria-label") || "").toLowerCase();
                const checked = sw.getAttribute("aria-checked") === "true";

                // If the inner input has an id, also map it
                const input = sw.querySelector<HTMLInputElement>('input[type=checkbox][id]');
                if (input?.id && computedFormData[input.id] === undefined) {
                  computedFormData[input.id] = checked;
                  previousValuesRef.current[input.id] = checked;
                }

                // Prefer mapping by field label/id
                const mappedId = aria ? labelToId.get(aria) : undefined;
                if (mappedId) {
                  computedFormData[mappedId] = checked;
                  previousValuesRef.current[mappedId] = checked;
                }
              });
            }
            const visible = !!f.visibleIf(computedFormData);
            return { ...f, visible };
          }
          return f;
        }) as T[];
        // Filtra os campos invisíveis para que não sejam renderizados
        return computedRow.filter(ff => (ff as unknown as { visible?: boolean }).visible !== false) as T[];
      });

    const wrapOnChange = (raw: (id: string, value: unknown) => void) =>
      (id: string, value: unknown) => {
        previousValuesRef.current[id] = value;
        raw(id, value);
      };

    // Ajustar cada FormFields para usar wrapOnChange
    if (isMultiStep) {
      const visibleStepRows = applyVisibleIfFields(stepFields as Field[][]);
      return (
        <>
          <FormFields
            fields={visibleStepRows as Field[][]}
            formData={formData}
            mode={mode}
            theme={theme}
            handleFocus={handleFocus} // <-- ADICIONADO
            handleFieldChange={wrapOnChange((id, value) => {
              handleFieldChangeWithValidation(
                id,
                value as unknown as string | boolean | File[] | undefined,
                fieldGroupsMatrix,
                onChange,
                onError,
                onValid
              );
              const field = allFields.find((f) => f.id === id);
              if (
                field &&
                Array.isArray(field.validators) &&
                field.validators.some(
                  (v) => v.type === "remote" || v.type === "async"
                )
              ) {
                triggerRemoteValidation(
                  field,
                  value,
                  { ...formData, [id]: value },
                  () => onValid && onValid(field.id),
                  (msg) => onError && onError(field.id, msg)
                );
              }
            })}
            handleBlur={(id, value) => {
              const field = allFields.find((f) => f.id === id);
              if (field) {
                handleBlurWithValidation(
                  field,
                  value as unknown as string | boolean | File[] | undefined
                );
                if (
                  Array.isArray(field.validators) &&
                  field.validators.some(
                    (v) => v.type === "remote" || v.type === "async"
                  )
                ) {
                  triggerRemoteValidation(
                    field,
                    value,
                    { ...formData, [field.id]: value },
                    () => onValid && onValid(field.id),
                    (msg) => onError && onError(field.id, msg)
                  );
                }
              }
            }}
            fieldStatus={fieldStatus}
            {...(config.columns ? { columns } : {})}
            renderCustomField={renderCustomFieldFn}
            remoteValidationStatus={remoteValidationStatus}
            showOptionalFields={showOptionalFields}
            fieldPropsOverride={fieldPropsOverrideForChild}
          />
          {renderStepNavigation()}
        </>
      );
    }

    // Accordion
    if (accordion) {
      return (
        <Accordion
          theme={theme}
          multiOpen={config.multiStep ? false : true}
          idBase={`form-accordion-${collectionName}`}
          data-testid="form-accordion"
        >
          {fieldsArray.map((fieldsGroup, idx) => {
            const groupFields = Array.isArray(fieldsGroup)
              ? fieldsGroup
              : (fieldsGroup as TabFieldsGroup<F>).fields;
            const groupLabel = Array.isArray(fieldsGroup)
              ? groupFields[0]?.label || `Seção ${idx + 1}`
              : (fieldsGroup as TabFieldsGroup<F>).tabLabel ||
                `Seção ${idx + 1}`;
            return (
              <Accordion.Item key={idx} index={idx}>
                <Accordion.Header
                  index={idx}
                  label={groupLabel}
                  data-testid={`form-accordion-header-${idx}`}
                  aria-label={groupLabel}
                >
                  {groupLabel}
                </Accordion.Header>
                <Accordion.Body
                  index={idx}
                  data-testid={`form-accordion-body-${idx}`}
                >
                  <FormFields
                    fields={applyVisibleIfFields([
                      normalizeFieldsContainerProps(groupFields) as Field[],
                    ])}
                    formData={formData}
                    mode={mode}
                    theme={theme}
                    handleFocus={handleFocus} // <-- ADICIONADO
                    handleFieldChange={wrapOnChange((id, value) =>
                      handleFieldChangeWithValidation(
                        id,
                        value as unknown as string | boolean | File[] | undefined,
                        fieldGroupsMatrix,
                        onChange,
                        onError,
                        onValid
                      )
                    )}
                    handleBlur={(id, value) => {
                      const field = allFields.find((f) => f.id === id);
                      if (field) {
                        handleBlurWithValidation(
                          field,
                          value as unknown as string | boolean | File[] | undefined // <-- ajustado
                        );
                        if (
                          Array.isArray(field.validators) &&
                          field.validators.some(
                            (v) => v.type === "remote" || v.type === "async"
                          )
                        ) {
                          triggerRemoteValidation(
                            field,
                            value,
                            { ...formData, [field.id]: value },
                            () => onValid && onValid(field.id),
                            (msg) => onError && onError(field.id, msg)
                          );
                        }
                      }
                    }}
                    fieldStatus={fieldStatus}
                    columns={columns}
                    renderCustomField={renderCustomFieldFn}
                    showOptionalFields={showOptionalFields}
                    fieldPropsOverride={fieldPropsOverrideForChild}
                  />
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      );
    }

    // Tabs
    if (tabs) {
      return (
        <Tab theme={theme}>
          <Tab.List>
            {fieldsArray.map((group, idx) => {
              const groupLabel = Array.isArray(group)
                ? group[0]?.label || `Aba ${idx + 1}`
                : (group as TabFieldsGroup<F>).tabLabel || `Aba ${idx + 1}`;
              return (
                <div
                  key={idx}
                  data-tab-index={idx}
                  data-testid={`form-tab-label-${idx}`}
                  className="form-tab-label px-4 py-2 cursor-pointer"
                >
                  {groupLabel}
                </div>
              );
            })}
          </Tab.List>
          {fieldsArray.map((group, idx) => {
            const fieldsGroup = Array.isArray(group)
              ? group
              : (group as TabFieldsGroup<F>).fields;
            return (
              <Tab.Panel key={idx} index={idx} className="form-tab-panel">
                <FormFields
                  fields={applyVisibleIfFields([
                    normalizeFieldsContainerProps(
                      fieldsGroup as unknown as Field[]
                    ) as Field[],
                  ])}
                  formData={formData}
                  mode={mode}
                  theme={theme}
                  handleFocus={handleFocus}
                  handleFieldChange={wrapOnChange((id, value) =>
                    handleFieldChangeWithValidation(
                      id,
                      value as unknown as string | boolean | File[] | undefined,
                      fieldGroupsMatrix,
                      onChange,
                      onError,
                      onValid
                    )
                  )}
                  handleBlur={(id, value) => {
                    const field = allFields.find((f) => f.id === id);
                    if (field) {
                      handleBlurWithValidation(
                        field,
                        value as unknown as string | boolean | File[] | undefined
                      );
                      if (
                        Array.isArray(field.validators) &&
                        field.validators.some(
                          (v) => v.type === "remote" || v.type === "async"
                        )
                      ) {
                        triggerRemoteValidation(
                          field,
                          value,
                          { ...formData, [field.id]: value },
                          () => onValid && onValid(field.id),
                          (msg) => onError && onError(field.id, msg)
                        );
                      }
                    }
                  }}
                  fieldStatus={fieldStatus}
                  columns={config.columns}
                  renderCustomField={renderCustomFieldFn}
                  remoteValidationStatus={remoteValidationStatus}
                  showOptionalFields={showOptionalFields}
                  fieldPropsOverride={fieldPropsOverrideForChild}
                />
              </Tab.Panel>
            );
          })}
        </Tab>
      );
    }

    // Layout simples
    return (
      <FormFields
        fields={applyVisibleIfFields(memoizedFields as Field[][])}
        formData={formData}
        mode={mode}
        theme={theme}
        handleFocus={handleFocus} // <-- ADICIONADO
        handleFieldChange={wrapOnChange((id, value) =>
          handleFieldChangeWithValidation(
            id,
            value as unknown as string | boolean | File[] | undefined,
            fieldGroupsMatrix,
            onChange,
            onError,
            onValid
          )
        )}
        handleBlur={(id, value) => {
          const field = allFields.find((f) => f.id === id);
          if (field) {
            handleBlurWithValidation(
              field,
              value as unknown as string | boolean | File[] | undefined // <-- ajustado
            );
            if (
              Array.isArray(field.validators) &&
              field.validators.some(
                (v) => v.type === "remote" || v.type === "async"
              )
            ) {
              triggerRemoteValidation(
                field,
                value,
                { ...formData, [field.id]: value },
                () => onValid && onValid(field.id),
                (msg) => onError && onError(field.id, msg)
              );
            }
          }
        }}
        fieldStatus={fieldStatus}
        columns={config.columns}
        className={undefined}
        style={undefined}
        renderCustomField={renderCustomFieldFn}
        onValid={onValid}
        remoteValidationStatus={remoteValidationStatus}
        showOptionalFields={showOptionalFields}
        fieldPropsOverride={fieldPropsOverrideForChild}
      />
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submete com Ctrl+Enter / Cmd+Enter (já existente)
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleButtonClick(mode === "create" ? "create" : mode === "update" ? "update" : "submit");
      return;
    }
    // Novo: Enter simples para submeter (exceto em textarea / multiStep)
    if (
      e.key === "Enter" &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.shiftKey &&
      !isMultiStep
    ) {
      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || "").toLowerCase();
      const isTextArea = tag === "textarea";
      if (isTextArea) return; // não intercepta textarea
      e.preventDefault();
      handleButtonClick(mode === "create" ? "create" : mode === "update" ? "update" : "submit");
    }
  };

  // NOVO: submit nativo do <form>
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleButtonClick(
      mode === "create" ? "create" : mode === "update" ? "update" : "submit"
    );
  };

  // Helper: Copiar dados atuais e acionar ação "copy-edit"
  function handleCopyAndEdit(
    actionCb: ((action: string, data: Record<string, unknown>) => void) | undefined,
    data: Record<string, unknown>
  ) {
    if (!actionCb) return;
    const cloned = { ...data };
    // Remove identificadores comuns para iniciar edição como novo
    delete (cloned as { id?: unknown }).id;
    delete (cloned as { _id?: unknown })._id;
    actionCb("copy-edit", cloned);
  }

  const buttonIcons: Record<string, React.ReactNode> = {
    submit: <MdSend style={{ marginRight: 8 }} />,
    create: <MdSave style={{ marginRight: 8 }} />,
    update: <MdEdit style={{ marginRight: 8 }} />,
    delete: <MdClear style={{ marginRight: 8 }} />,
    "Salvar Rascunho": <MdAssignment style={{ marginRight: 8 }} />,
    Limpar: <MdClear style={{ marginRight: 8 }} />,
    Resetar: <MdRefresh style={{ marginRight: 8 }} />,
    "Patch (parcial)": <MdWatch style={{ marginRight: 8 }} />,
    "Setar Valores": <MdAssignment style={{ marginRight: 8 }} />,
  };

  function t(title: string): string {
    return title;
  }
  const renderButtons = () => {
    const buttonVariant = config.buttonVariant;
    const statusButtons = (config.buttons?.[mode] || []).concat(
      config.buttons?.custom || []
    );
    const customCopyEditButton =
      mode === "read"
        ? [
            {
              title: "Copiar e Editar",
              action: "copy-edit",
              icon: <MdEdit style={{ marginRight: 8 }} />,
              custom: true,
              onClick: () => handleCopyAndEdit(onAction, formData),
            },
          ]
        : [];
    const allButtons = statusButtons.concat(customCopyEditButton);
    const isSubmitAction = (action: string) =>
      action === "create" || action === "update" || action === "submit";

    if (buttonVariant === "fab") {
      // NOVO: usa prop 'actions' do FabMenu
      const actions = allButtons.map((b, idx) => ({
        key: b.action || String(idx),
        label: t(b.title),
        icon:
          typeof b.icon === "string"
            ? (buttonIcons[b.icon] as React.ReactElement)
            : (b.icon as React.ReactElement),
        tooltip: t(b.title),
        disabled: enableRecaptcha ? !recaptchaValidated : false,
        onClick: () => {
          if (b.onClick) {
            b.onClick(formData);
          } else {
            handleButtonClick(b.action);
          }
        },
      }));
      return (
        <FabMenu
          label="Ações rápidas"
            direction="up"
            theme={theme}
            actions={actions}
            closeOnAction
        />
      );
    }

    return (
      <div className="flex flex-wrap gap-3 mt-6 justify-end">
        {allButtons.map((button, index) => {
          const submitLike = isSubmitAction(button.action);
          return (
            <Button
              key={index}
              type="button"
              theme={theme}
              icon={
                typeof button.icon === "string"
                  ? buttonIcons[button.icon]
                  : button.icon
              }
              title={t(button.title)}
              onClick={() => {
                if (button.onClick) {
                  button.onClick(formData);
                } else {
                  handleButtonClick(button.action);
                }
              }}
              disabled={enableRecaptcha ? !recaptchaValidated : false}
              tooltip={isTestEnv ? undefined : t(button.title)}
              className="min-w-[120px]"
              data-testid={`form-btn-${button.action}${submitLike ? "-submitlike" : ""}`}
            />
          );
        })}
      </div>
    );
  };

  // --- Não existe mais Toast nem ToastMessagesContext.Provider aqui! ---

  const [helpOpen, setHelpOpen] = useState(false);

  // Foco automático no primeiro campo se autoFocusFirstField for true
  useEffect(() => {
    if (autoFocusFirstField) {
      const formEl = document.querySelector('[data-form-root="true"]');
      if (formEl) {
        const candidates = Array.from(
          formEl.querySelectorAll<HTMLElement>(
            'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
          )
        ).filter((el) => {
          if (el.getAttribute("type") === "checkbox") return false; // ignora toggle interno
          if (el.tabIndex === -1) return false;
          return true;
        });
        candidates[0]?.focus();
      }
    }
  }, [autoFocusFirstField]);

  // Força re-render após qualquer clique em switches em testes para recalcular visibleIf
  useEffect(() => {
    if (process.env.NODE_ENV !== "test") return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target && target.getAttribute("role") === "switch") {
        __setVisTick(v => v + 1);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  // NOVO: Em testes, sincroniza aria-checked dos toggles com formData (por label/id do campo)
  useEffect(() => {
    if (process.env.NODE_ENV !== "test") return;

    // Mapeia label normalizado -> id do campo toggle
    const labelToId = new Map<string, string>();
    allFields
      .filter((f) => (f as FormField).type === "toggle")
      .forEach((f) => {
        const lbl = (f.label || f.id || "").toString().toLowerCase();
        if (lbl) labelToId.set(lbl, f.id);
        // Também aceita o próprio id como "label"
        labelToId.set(f.id.toLowerCase(), f.id);
      });

    const syncFromSwitches = () => {
      const updates: Record<string, unknown> = {};
      document.querySelectorAll<HTMLElement>('[role="switch"]').forEach((sw) => {
        const aria = (sw.getAttribute("aria-label") || "").toLowerCase();
        const id = labelToId.get(aria);
        if (id) {
          const checked = sw.getAttribute("aria-checked") === "true";
          updates[id] = checked;
        }
      });
      if (Object.keys(updates).length) setValues(updates);
    };

    // Sincroniza em cada clique (captura), cobrindo toggles renderizados
    document.addEventListener("click", syncFromSwitches, true);
    // Sincroniza uma vez após montar (caso existam toggles já marcados)
    Promise.resolve().then(syncFromSwitches);

    return () => {
      document.removeEventListener("click", syncFromSwitches, true);
    };
  }, [allFields, setValues]);

  const formContent = (
    <div
      className={clsx(
        "w-full focus:outline-none",
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-white text-gray-900",
        "rounded-xl p-6 transition-colors duration-200",
        theme === "dark" ? "fluent-dark" : "fluent-light"
      )}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-form-root="true"
      data-theme={theme}
    >
      <div className="mb-6 relative">
        <div className="flex flex-col">
          <h2
            className={clsx(
              "text-2xl font-semibold tracking-tight mb-2",
              theme === "dark"
                ? "text-gray-100"
                : "text-gray-900"
            )}
            style={{ textAlign: "center" }}
          >
            {t(config.title)}
          </h2>
          <div className="flex items-center justify-end gap-2 mt-2">
            <Toggle
              value={showOptionalFields}
              onValueChange={setShowOptionalFields}
              label="Campos opcionais"
              theme={theme}
              data-testid="toggle-optional-fields"
            />
            {helpMarkdown && (
              <button
                type="button"
                aria-label="Ajuda"
                title="Ajuda"
                onClick={() => setHelpOpen(true)}
                className={clsx(
                  "p-2",
                  "bg-transparent border-none cursor-pointer",
                  theme === "dark" ? "text-gray-200" : "text-gray-700",
                  "text-2xl leading-none"
                )}
                data-testid="form-help-button"
              >
                <MdHelpOutline />
              </button>
            )}
          </div>
        </div>
      </div>
      {renderFields()}
      {!isMultiStep && renderButtons()}
      {/* DEBUG VISUAL */}
      {debug && (
        <div
          style={{
            marginTop: 24,
            background: "#222",
            color: "#fff",
            borderRadius: 8,
            padding: 16,
            fontSize: 13,
            fontFamily: "monospace",
            maxHeight: 400,
            overflow: "auto",
          }}
          data-testid="form-debug-panel"
        >
          <strong>Config final do formulário:</strong>
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(config, null, 2)}
          </pre>
          {debugWarnings && debugWarnings.length > 0 && (
            <div style={{ marginTop: 16, color: "#ffb347" }}>
              <strong>Debug Warnings:</strong>
              <ul>
                {debugWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {helpMarkdown && (
        <Modal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          theme={theme}
          header="Ajuda"
          outlined
          shadow="md"
          aria-label="Ajuda do formulário"
        >
          <div className="max-h-96 overflow-y-auto p-2">
            <ReactMarkdown>{helpMarkdown}</ReactMarkdown>
          </div>
        </Modal>
      )}
    </div>
  );

  if (enableRecaptcha && recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
        <form onSubmit={handleFormSubmit} noValidate>
          {formContent}
        </form>
      </GoogleReCaptchaProvider>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} noValidate>
      {formContent}
    </form>
  );
};

// Mapeadores customizados de campos do screens-api
type FieldMapper = (valleField: unknown) => Partial<FormField> | Promise<Partial<FormField>>;
const customFieldMappers: Record<string, FieldMapper> = {};

/**
 * Permite registrar um mapeador customizado para tipos de campo do screens-api.
 * Agora aceita funções síncronas ou assíncronas.
 * Exemplo:
 *   Form.registerFieldMapper("autocomplete", async (valleField) => {
 *     const options = await fetchOptionsFromAPI();
 *     return { ...valleField, options };
 *   });
 */
function registerFieldMapper(type: string, mapper: FieldMapper) {
  customFieldMappers[type] = mapper;
}

/**
 * --- LIMPEZA: remover bloco comentado duplicado de debugWarnings ---
 */
let debugWarnings: string[] = [];

/**
 * Patch na função de conversão para usar os mapeadores registrados.
 * Agora suporta mapeadores assíncronos.
 */
async function convertScreensApiToFormConfigWithCustomMappers(
  valleJson: ValleScreenApiJson
): Promise<FormConfig> {
  debugWarnings = [];
  const base = convertScreensApiToFormConfig(valleJson) as FormConfig;
  const mapField = async (field: FormField): Promise<FormField> => {
    if (field?.type && customFieldMappers[field.type]) {
      try {
        const mapped = await customFieldMappers[field.type](field);
        return { ...field, ...mapped };
      } catch (e) {
        debugWarnings.push(
          `Erro ao mapear campo do tipo "${field.type}" (id: ${field.id}): ${
            e instanceof Error ? e.message : String(e)
          }`
        );
        return field;
      }
    } else if (field?.type && !customFieldMappers[field.type]) {
      debugWarnings.push(
        `Campo do tipo "${field.type}" (id: ${field.id}) não possui mapeador customizado registrado.`
      );
    }
    return field;
  };

  const mappedFields = await Promise.all(
    (base.fields || []).map(async (group) => {
      if (Array.isArray(group)) {
        return Promise.all(group.map(mapField));
      }
      const tab = group as TabFieldsGroup;
      if (Array.isArray(tab.fields)) {
        return {
          ...tab,
          fields: await Promise.all(tab.fields.map(mapField)),
        };
      }
      return group;
    })
  );

  return { ...base, fields: mappedFields };
}

// Exporta função de conversão
export { convertScreensApiToFormConfigWithCustomMappers };

// Tipagem do componente principal
type FormComponentType = <F extends Field = Field>(
  props: FormProps<F> & { ref?: React.Ref<FormHandle> }
) => ReturnType<typeof FormInner>;

// Instância principal
const FormComponent = forwardRef(FormInner) as FormComponentType;

// Métodos utilitários anexados
// Removed duplicate block: Form re-declaration and helper duplicates were causing "Cannot redeclare block-scoped variable 'Form'".
Object.assign(FormComponent, {
  fromScreensApi: convertScreensApiToFormConfigWithCustomMappers,
  registerFieldMapper,
  mergeConfig(
    baseConfig: FormConfig,
    overrides: Partial<FormConfig>
  ): FormConfig {
    return {
      ...baseConfig,
      ...overrides,
      fields: overrides.fields || baseConfig.fields,
      buttons: { ...baseConfig.buttons, ...overrides.buttons },
    };
  },
  debugWarnings,
});

const Form = FormComponent;
export { Form };

/**
 * Normaliza linha de campos (garante id estável).
 */
function normalizeFieldsContainerProps<F extends FormField>(
  row: F[]
): (FormField & { id: string })[] {
  return row
    .filter((f) => f && typeof f.id === "string")
    .map((f) => ({ ...f, id: f.id }));
}

/**
 * Armazena última resolução de fields pós normalização assíncrona.
 */
let __lastResolvedFieldsArray: (TabFieldsGroup | FormField[])[] | undefined;
const __resolvedFieldsListeners = new Set<
  (val: (TabFieldsGroup | FormField[])[]) => void
>();

export function onResolvedFieldsArrayChange(
  listener: (val: (TabFieldsGroup | FormField[])[]) => void
): () => void {
  __resolvedFieldsListeners.add(listener);
  if (__lastResolvedFieldsArray) listener(__lastResolvedFieldsArray);
  return () => __resolvedFieldsListeners.delete(listener);
}

function setResolvedFieldsArray(r: (TabFieldsGroup | FormField[])[]) {
  if (!Array.isArray(r)) return;
  __lastResolvedFieldsArray = r;
  __resolvedFieldsListeners.forEach((cb) => {
    try {
      cb(r);
    } catch {
      /* ignore */
    }
  });
}